import type { AuthHelpers } from './types';
export const useAuthHelpers = (): AuthHelpers => {
  const setTokenCookies = (token: Token) => {
    const accessTokenCookie = useCookie('token', {
      maxAge: token.expireAt - Math.floor(Date.now() / 1000),
    });
    accessTokenCookie.value = token.accessToken;

    const refreshTokenCookie = useCookie('refreshToken', {
      maxAge: 30 * 60,
      path: '/',
    });
    refreshTokenCookie.value = token.refreshToken;
  };

  const getTokenExpireDate = (token: string): number | null => {
    try {
      const payloadBase64 = token.split('.')[1];
      const payload = JSON.parse(atob(payloadBase64));
      return payload.exp ?? null;
    } catch (e) {
      console.error('Invalid token:', e);
      return null;
    }
  };
  
  const setTokenCookiesFromToken = (token: string) => {
    const exp = getTokenExpireDate(token);
    const now = Math.floor(Date.now() / 1000);
  
    if (!exp || exp <= now) {
      console.warn('Token is expired or invalid');
      return;
    }
  
    const maxAge = exp - now;
    const accessTokenCookie = useCookie('token', { maxAge });
    accessTokenCookie.value = token;
  };

  const setAccountCookies = (customerAcccountIdValue: string) => {
    const customerAccountIdCookie = useCookie('customer-account-id');
    customerAccountIdCookie.value = customerAcccountIdValue;
  };
  const setStoreCookies = (storeId: string) => {
    const storeIdCookie = useCookie('store-id');
    storeIdCookie.value = storeId;
  };
  const setStoreViewCookies = (storeViewId: string) => {
    const storeViewIdCookie = useCookie('store-view-id');
    storeViewIdCookie.value = storeViewId;
  };
  const setCurrencyCookies = (currency: string) => {
    const currencyCookie = useCookie('currency');
    currencyCookie.value = currency;
  };

  const clearTokenCookies = () => {
    useCookie('token').value = null;
    useCookie('refreshToken').value = null;
    useCookie('customer-account-id').value = null;
    useCookie('store-id').value = null;
    useCookie('store-view-id').value = null;
    useCookie('currency').value = null;
  };

  const ensureAuthenticated = async (): Promise<string> => {
    const jwtCookie = useCookie<string | null>('token');
    const refreshCookie = useCookie<string | null>('refreshToken');
    const now = Math.floor(Date.now() / 1000);

    function decodeJwt(token: string): any | null {
      try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        return JSON.parse(atob(parts[1]));
      } catch (error) {
        console.error('Error decoding JWT:', error);
        return null;
      }
    }

    // Check if token is valid
    if (jwtCookie.value) {
      const decodedJwt = decodeJwt(jwtCookie.value);
      if (decodedJwt?.exp && now < decodedJwt.exp) {
        return jwtCookie.value; // Token is still valid
      }
    }

    // Try to refresh the token if refreshToken exists
    if (refreshCookie.value) {
      try {
        const { data } = await useFetch<{
          isAuthenticated: boolean;
          token: string;
          refreshToken: string;
        }>('/api/auth/refresh', {
          method: 'POST',
          body: { refreshToken: refreshCookie.value },
        });

        if (data.value?.isAuthenticated && data.value.token && data.value.refreshToken) {
          jwtCookie.value = data.value.token;
          refreshCookie.value = data.value.refreshToken;
          return data.value.token;
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
      }
    }

    // If refresh fails, reset authentication state
    try {
      await useFetch<{ success: boolean }>('/api/auth/reset-config', {
        method: 'POST',
      });
    } catch (error) {
      console.error('Error resetting auth config:', error);
    }

    throw new Error('Authentication required. Please log in again.');
  };

  return {
    setTokenCookies,
    setAccountCookies,
    setStoreCookies,
    setStoreViewCookies,
    setCurrencyCookies,
    clearTokenCookies,
    ensureAuthenticated,
    setTokenCookiesFromToken,
  };
};
